import React, { useState } from 'react';
import { X, Plus, ChevronUp, ChevronDown, PlusCircle } from 'lucide-react';

const EditRecipeModal = ({ recipe, onClose, onSave }) => {
  const [editedRecipe, setEditedRecipe] = useState(recipe);
  const [saving, setSaving] = useState(false);

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...editedRecipe.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setEditedRecipe({ ...editedRecipe, ingredients: newIngredients });
  };

  const handleAddIngredient = () => {
    setEditedRecipe({
      ...editedRecipe,
      ingredients: [...editedRecipe.ingredients, { name: '', quantity: '', unit: '', category: 'pantry' }],
    });
  };

  const handleRemoveIngredient = (index) => {
    setEditedRecipe({
      ...editedRecipe,
      ingredients: editedRecipe.ingredients.filter((_, i) => i !== index),
    });
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...editedRecipe.instructions];
    newInstructions[index] = { ...newInstructions[index], text: value };
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const handleAddInstruction = () => {
    setEditedRecipe({
      ...editedRecipe,
      instructions: [
        ...editedRecipe.instructions,
        { step_number: editedRecipe.instructions.length + 1, text: '' },
      ],
    });
  };

  const handleRemoveInstruction = (index) => {
    const newInstructions = editedRecipe.instructions
      .filter((_, i) => i !== index)
      .map((inst, i) => ({ ...inst, step_number: i + 1 }));
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const handleMoveInstruction = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editedRecipe.instructions.length) return;
    
    const newInstructions = [...editedRecipe.instructions];
    // Swap the instructions
    [newInstructions[index], newInstructions[newIndex]] = [newInstructions[newIndex], newInstructions[index]];
    // Renumber all steps
    const renumbered = newInstructions.map((inst, i) => ({ ...inst, step_number: i + 1 }));
    setEditedRecipe({ ...editedRecipe, instructions: renumbered });
  };

  const handleInsertInstruction = (afterIndex) => {
    const newInstructions = [...editedRecipe.instructions];
    // Insert a new empty instruction after the specified index
    newInstructions.splice(afterIndex + 1, 0, { step_number: afterIndex + 2, text: '' });
    // Renumber all steps
    const renumbered = newInstructions.map((inst, i) => ({ ...inst, step_number: i + 1 }));
    setEditedRecipe({ ...editedRecipe, instructions: renumbered });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedRecipe);
    } catch (error) {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Edit Recipe</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-edits-button"
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipe Title *</label>
              <input
                type="text"
                value={editedRecipe.title}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={editedRecipe.description || ''}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prep (min)</label>
                <input
                  type="number"
                  value={editedRecipe.prep_time || ''}
                  onChange={(e) =>
                    setEditedRecipe({ ...editedRecipe, prep_time: parseInt(e.target.value) || null })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cook (min)</label>
                <input
                  type="number"
                  value={editedRecipe.cook_time || ''}
                  onChange={(e) =>
                    setEditedRecipe({ ...editedRecipe, cook_time: parseInt(e.target.value) || null })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Servings</label>
                <input
                  type="number"
                  value={editedRecipe.servings || ''}
                  onChange={(e) =>
                    setEditedRecipe({ ...editedRecipe, servings: parseInt(e.target.value) || null })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={editedRecipe.category || 'other'}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="main_dish">Main Dish</option>
                  <option value="appetizer">Appetizer</option>
                  <option value="dessert">Dessert</option>
                  <option value="salad">Salad</option>
                  <option value="soup">Soup</option>
                  <option value="side">Side</option>
                  <option value="breakfast">Breakfast</option>
                  <option value="snack">Snack</option>
                  <option value="beverage">Beverage</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Ingredients</h3>
              <button
                onClick={handleAddIngredient}
                className="flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              >
                <Plus size={20} />
                Add
              </button>
            </div>
            <div className="space-y-3">
              {editedRecipe.ingredients.map((ing, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={ing.quantity}
                    onChange={(e) => handleIngredientChange(idx, 'quantity', e.target.value)}
                    placeholder="Amount"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                  <input
                    type="text"
                    value={ing.unit}
                    onChange={(e) => handleIngredientChange(idx, 'unit', e.target.value)}
                    placeholder="Unit"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                  <input
                    type="text"
                    value={ing.name}
                    onChange={(e) => handleIngredientChange(idx, 'name', e.target.value)}
                    placeholder="Ingredient"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    onClick={() => handleRemoveIngredient(idx)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Instructions</h3>
              <button
                onClick={handleAddInstruction}
                className="flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              >
                <Plus size={20} />
                Add Step
              </button>
            </div>
            <div className="space-y-3">
              {editedRecipe.instructions.map((inst, idx) => (
                <div key={idx} className="group">
                  <div className="flex gap-2">
                    {/* Move buttons */}
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleMoveInstruction(idx, 'up')}
                        disabled={idx === 0}
                        className={`p-1 rounded transition-colors ${
                          idx === 0 
                            ? 'text-gray-200 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                        }`}
                        title="Move up"
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button
                        onClick={() => handleMoveInstruction(idx, 'down')}
                        disabled={idx === editedRecipe.instructions.length - 1}
                        className={`p-1 rounded transition-colors ${
                          idx === editedRecipe.instructions.length - 1 
                            ? 'text-gray-200 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                        }`}
                        title="Move down"
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                    {/* Step number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center font-semibold text-amber-600">
                      {inst.step_number}
                    </div>
                    {/* Text area */}
                    <textarea
                      value={inst.text}
                      onChange={(e) => handleInstructionChange(idx, e.target.value)}
                      placeholder="Describe this step..."
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                    {/* Delete button */}
                    <button
                      onClick={() => handleRemoveInstruction(idx)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors self-start"
                      title="Remove step"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  {/* Insert step button - appears between steps on hover */}
                  <button
                    onClick={() => handleInsertInstruction(idx)}
                    className="w-full mt-2 py-1 flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                    title="Insert step below"
                  >
                    <PlusCircle size={14} />
                    <span>Insert step here</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecipeModal;
